import { useState } from 'react';
import {
  useSelectedPlant,
  useSelectedLocation,
  usePlants,
  useLocations,
  useQueues,
  useCreateAction,
  useUpdateAction,
  useAction,
  useSelectionLists,
  useTransactions,
  useDispositions,
  useActions,
} from '../../../../hooks';
import { QUERY_TYPES } from '../../../../data/constants';

import { SetupDrawer } from '../../../atoms';
import { TextInput, Select, toast, Switch } from '@pella/sash-framework';
import classes from './Action.module.css';

const baseTenForSequence = 10;

/**
 * Validates the sequence input.
 * @param {string} value - The value to validate.
 * @returns {boolean} - True if there's an error, false otherwise.
 */
const errorCheckSequence = (value) => {
  const num = Number(value);
  return Number.isNaN(num) || num < 1 || num > 1000;
};

/**
 * Initial form error state for region location validation.
 * @type {{
 *   sequence: boolean,
 * }}
 */
const initialFormErrors = {
  sequence: false,
};

const TRANSACTIONS_REQUIRING_INPUT = new Set(['DIRECT_ORG_TRANSFER', 'SUB_INVENTORY_TRANSFER', 'ISSUE_TO_LINE']);

/**
 * Validates the action form and returns an error message if invalid.
 * @param {Local.ActionFormData} form - The action form to validate.
 * @returns {string | null} - Error message if invalid, null if valid.
 */
const validateActionForm = (form) => {
  if (form.sequence < 1) {
    return 'Sequence must be greater than 0';
  }

  if (form.sequence > 1000) {
    return 'Sequence must be less than or equal to 1000';
  }

  const hasRequiredTransaction = form.transactionId ? TRANSACTIONS_REQUIRING_INPUT.has(form.transactionId) : false;
  const hasInputOrList = form.selectionListId1 || form.showDynamicInput;

  // A query (with or without a transaction) requires input/selection list
  if (form.actionQuery && !hasInputOrList) {
    return 'The selected transaction and or query type requires Input or Selection List to be enabled. Please enable one of these options or remove the transaction type to add this Action.';
  }

  // A required transaction requires input/selection list
  if (hasRequiredTransaction && !hasInputOrList) {
    return 'The selected transaction type requires Input or Selection List to be enabled. Please enable one of these options or remove the transaction type to add this Action.';
  }

  return null; // Valid
};

/**
 * Validates that this Action has Input or Selection List enabled whenever the selected
 * Linked Action does, mirroring the server-side rule in actionValidation.js.
 * @param {Local.ActionFormData} form - The action form to validate.
 * @param {Pella.ProductionRequestAPI.Action | null | undefined} selectedLinkedAction - The resolved Linked Action, if one is selected.
 * @returns {string | null} - Error message if invalid, null if valid.
 */
const validateLinkedAction = (form, selectedLinkedAction) => {
  if (!selectedLinkedAction) {
    return null;
  }

  const linkedHasInputOrList = selectedLinkedAction.showDynamicInput || selectedLinkedAction.showSelectionList;
  const formHasInputOrList = form.showDynamicInput || form.showSelectionList;

  if (linkedHasInputOrList && !formHasInputOrList) {
    return 'The Linked Action has Input or Selection List enabled. Input or Selection List must be enabled on this Action to assign the Linked Action.';
  }

  return null; // Valid
};

/**
 * Component for the Action Drawer.
 * @param {Local.SetupDrawerProps} props - Component props
 */
export const ActionDrawer = ({ open, close, data, selectedId, isCopyMode }) => {
  const { data: actionData } = useAction(selectedId);
  const action = /** @type {Local.ActionWithLinkedAction | undefined} */ (actionData);
  const { data: transactions } = useTransactions();
  const { data: dispositions } = useDispositions();

  // State for user-modified form fields
  const [selectedQueuePlant, setSelectedQueuePlant] = useState(/** @type {string | null} */ (null));
  const [selectedQueuePlantQueue, setSelectedQueuePlantQueue] = useState('');

  const [formChanges, setFormChanges] = useState(/** @type {Partial<Local.ActionFormData>} */ ({}));

  const [formErrors, setFormErrors] = useState(initialFormErrors);

  // The location-based record (from `data`, via getActionsByLocation) carries queueWithPlant.
  // getActionById (`action`) does not include the plant, so the plant must come from here.
  const editedRecord = selectedId ? data?.find((record) => record.id === selectedId) : undefined;

  // Compute the effective queue plant ID for the queues hook
  const effectiveQueuePlantId = selectedQueuePlant !== null ? selectedQueuePlant : editedRecord?.queue?.plant?.id || '';

  const { data: queues } = useQueues(effectiveQueuePlantId, false);
  const isEdit = Boolean(selectedId && !isCopyMode);

  const { mutate: createAction, isPending: isCreating } = useCreateAction();
  const { mutate: updateAction, isPending: isUpdating } = useUpdateAction();

  const { selectedPlant } = useSelectedPlant();
  const { selectedLocation } = useSelectedLocation();

  const [localSelectedPlant, setLocalSelectedPlant] = useState(selectedPlant);
  const [localSelectedLocation, setLocalSelectedLocation] = useState('');

  // Cascading selection for the action this action links to. Kept separate from the routing
  // selection above so linking can reach an action in any plant, not just the one being edited.
  // Sentinels start `null` (not '') so the effective values below can fall back to the loaded
  // action's own linked action until the user actually touches one of these selects.
  const [linkedActionPlant, setLinkedActionPlant] = useState(/** @type {string | null} */ (null));
  const [linkedActionLocation, setLinkedActionLocation] = useState(/** @type {string | null} */ (null));
  const [linkedActionId, setLinkedActionId] = useState(/** @type {string | null} */ (null));

  // Same pattern as effectiveQueuePlantId above: prefer the user's explicit selection, otherwise
  // fall back to the linked action already saved on the record being edited or copied.
  const effectiveLinkedActionPlant =
    linkedActionPlant !== null ? linkedActionPlant : action?.linkedAction?.location?.plant?.id || '';
  const effectiveLinkedActionLocation =
    linkedActionLocation !== null ? linkedActionLocation : action?.linkedAction?.location?.id || '';
  const effectiveLinkedActionId = linkedActionId !== null ? linkedActionId : action?.linkedActionId || '';

  const { data: plants } = usePlants(true);
  const { data: locations } = useLocations(true, localSelectedPlant);
  const { data: actions } = useActions({ location: localSelectedLocation });
  const { data: selectionLists } = useSelectionLists(localSelectedPlant);
  const { data: linkedLocations } = useLocations(true, effectiveLinkedActionPlant);
  // Deliberately unfiltered so that, until a linked location is picked, this shares a query with
  // the call above rather than fetching the same actions a second time. Active is filtered below.
  const { data: linkedActions } = useActions({ location: effectiveLinkedActionLocation });

  // Both hooks fall back to the context plant/location when none is passed, so gate each list on
  // its own selection. An action cannot call itself, so drop the record being edited or copied.
  const linkedLocationOptions = effectiveLinkedActionPlant ? linkedLocations : [];
  const linkedActionOptions = effectiveLinkedActionLocation
    ? linkedActions?.filter((linked) => linked.active && linked.id !== selectedId)
    : [];

  /**
   *  Reset state during render when switching between different actions or reopening
   */
  const resetFormState = () => {
    setFormChanges({});
    setSelectedQueuePlant(null);
    setSelectedQueuePlantQueue('');
    setLocalSelectedPlant(selectedPlant);
    setLocalSelectedLocation('');
    setLinkedActionPlant(null);
    setLinkedActionLocation(null);
    setLinkedActionId(null);
  };

  /**
   * Calculate the default sequence based on current context.
   * In copy mode, starts empty until a location is selected, then uses the actions from that location.
   * Otherwise, uses the data prop.
   */
  const defaultSequence = (() => {
    if (isCopyMode) {
      // In copy mode, start with empty string until a location is selected
      if (!localSelectedLocation) {
        return '';
      }
      return actions ? actions.length * baseTenForSequence + baseTenForSequence : baseTenForSequence;
    }
    return data ? data.length * baseTenForSequence + baseTenForSequence : baseTenForSequence;
  })();

  // Spread `action` (getActionById) first so its showOnHoldReason and other dynamic fields
  // survive, then overlay `editedRecord` (getActionsByLocation) whose queue carries the plant.
  const currentForm = selectedId
    ? {
        ...action,
        ...editedRecord,
        ...(isCopyMode && { sequence: defaultSequence }),
      }
    : {
        active: true,
        // sequence always gets preset to length of data + 10. 5 actions means the next one is defaulted to 60
        sequence: defaultSequence,
        queueId: '',
        description: '',
        message: '',
        id: '',
      };

  // Combine initial data with form changes to get the current selection list state
  // Prioritize explicitly set queue changes, then fall back to state and existing data.
  // `editedRecord` is the last fallback so the Queue still displays when the direct
  // getActionById call is slow or unavailable — only the location record is guaranteed here.
  const queueId =
    'queue' in formChanges
      ? formChanges.queue?.id
      : selectedQueuePlantQueue || action?.queue?.id || editedRecord?.queue?.id || '';

  // ActionFormData type - combines base form, changes, and queue state
  const actionForm = /** @type {Local.ActionFormData} */ ({
    ...currentForm,
    ...formChanges,
    queue: {
      ...action?.queue,
      ...editedRecord?.queue,
      id: queueId,
    },
  });

  /**
   * Handles input change events for the form.
   * @param {string} field - The name of the field being updated.
   * @param {string | number | boolean | object | null} value - The new value for the field. this type feels unhelpful due to the dynamic nature of the input
   */
  const handleInputChange = (field, value) => {
    setFormChanges((prev) => ({ ...prev, [field]: value }));
  };

  /** Handler to reset form and close drawer */
  const handleClose = () => {
    setFormErrors(initialFormErrors);
    resetFormState();
    close();
  };

  const hasRequiredFields = Boolean(
    actionForm.queue?.id &&
    actionForm.message &&
    actionForm.description &&
    actionForm.sequence &&
    (actionForm.showDynamicInput ? actionForm.actionPrompt : true) &&
    (actionForm.showSelectionList ? actionForm.selectionListId1 : true) &&
    (actionForm.transactionId === 'TRAILER_TO_DOCK' ? actionForm.dockSelectionListId : true) &&
    (actionForm.transactionId === 'ISSUE_TO_LINE' ? actionForm.dispositionId : true) &&
    (isCopyMode ? localSelectedLocation : true),
  );

  const isLoading = isCreating || isUpdating;

  const disableSubmit = !hasRequiredFields || isLoading;

  /** handler for form submission */
  const handleSubmit = () => {
    const selectedLinkedAction = effectiveLinkedActionId
      ? linkedActions?.find((linked) => linked.id === effectiveLinkedActionId)
      : null;
    const validationError = validateActionForm(actionForm) || validateLinkedAction(actionForm, selectedLinkedAction);

    if (validationError) {
      toast.error(validationError);
      return;
    }

    const selectionList1 = actionForm?.selectionListId1?.length ? actionForm.selectionListId1 : null;
    const selectionList2 = actionForm?.selectionListId2?.length ? actionForm.selectionListId2 : null;

    const input = {
      active: actionForm.active,
      queueId: String(actionForm.queue?.id),
      message: actionForm.message,
      description: actionForm.description,
      locationId: isCopyMode ? localSelectedLocation : selectedLocation,
      sequence: Number(actionForm.sequence),
      showComment: actionForm.showComment ?? false,
      showQuantity: actionForm.showQuantity ?? false,
      showSelectionList: actionForm.showSelectionList ?? false,
      showDynamicInput: actionForm.showDynamicInput ?? false,
      isDefault: actionForm.isDefault ?? false,
      actionPrompt: actionForm.actionPrompt || null,
      selectionListId1: selectionList1,
      selectionListId2: selectionList2,
      transactionId: actionForm.transactionId || null,
      dockSelectionListId: actionForm.dockSelectionListId || null,
      dispositionId: actionForm.dispositionId ? Number(actionForm.dispositionId) : null,
      actionQuery: actionForm.actionQuery || null,
      showOnHoldReason: actionForm.showOnHoldReason ?? false,
      linkedActionId: effectiveLinkedActionId || null,
    };

    if (!isEdit) {
      createAction(input, {
        onSuccess: handleClose,
      });
    } else {
      updateAction(
        { id: selectedId, input },
        {
          onSuccess: handleClose,
        },
      );
    }
  };

  /**
   * Handles queue change event.
   * @param {React.ChangeEvent<HTMLInputElement | HTMLSelectElement>} e - The change event from the queue select input.
   */
  const handleQueueChange = (e) => {
    setSelectedQueuePlantQueue(e.target.value);
    handleInputChange('queue', { id: e.target.value });
  };

  /**
   * Updates the action form data state when an input changes.
   * @param {React.ChangeEvent<HTMLInputElement | HTMLSelectElement>} e - The change event from the input.
   */
  const updateActionForm = (e) => {
    const { name, value } = e.target;
    setFormChanges((prev) => ({ ...prev, [name]: value }));

    /** @type {Record<string, (value: string) => boolean>} */
    const errorFnMap = {
      sequence: errorCheckSequence,
    };
    setFormErrors((prev) => ({ ...prev, [name]: errorFnMap[name](value) }));
  };

  /**
   * handles local plant change
   * @param {React.ChangeEvent<HTMLInputElement | HTMLSelectElement>} e
   */
  const handlePlantChange = (e) => {
    setLocalSelectedPlant(e.target.value);
    setLocalSelectedLocation(''); // Reset location when plant changes
  };

  /**
   * Handles location change and resets sequence to default for the new location
   * @param {React.ChangeEvent<HTMLInputElement | HTMLSelectElement>} e
   */
  const handleLocationChange = (e) => {
    setFormErrors(initialFormErrors);
    setLocalSelectedLocation(e.target.value);
    // Clear any manually set sequence so it recalculates from defaultSequence
    setFormChanges((prev) => {
      const { sequence, ...rest } = prev;
      return rest;
    });
  };

  /**
   * Handles queue plant change to control queue values
   * @param {React.ChangeEvent<HTMLInputElement | HTMLSelectElement>} e - the change event from the queue plant select input
   */
  const handleSelectedQueuePlantChange = (e) => {
    setSelectedQueuePlant(e.target.value);
    setSelectedQueuePlantQueue('');
    handleInputChange('queue', { id: undefined });
  };

  /**
   * Handles the linked action plant change, clearing the location and action below it
   * @param {React.ChangeEvent<HTMLInputElement | HTMLSelectElement>} e - the change event from the linked action plant select input
   */
  const handleLinkedActionPlantChange = (e) => {
    setLinkedActionPlant(e.target.value);
    setLinkedActionLocation('');
    setLinkedActionId('');
  };

  /**
   * Handles the linked action location change, clearing the action below it
   * @param {React.ChangeEvent<HTMLInputElement | HTMLSelectElement>} e - the change event from the linked action location select input
   */
  const handleLinkedActionLocationChange = (e) => {
    setLinkedActionLocation(e.target.value);
    setLinkedActionId('');
  };

  /**
   * Handles dynamic input toggle, resetting the input prompt if disabling
   */
  const handleDynamicInput = () => {
    if (actionForm.showDynamicInput) {
      handleInputChange('actionPrompt', '');
    }
    handleInputChange('showDynamicInput', !actionForm.showDynamicInput);
  };

  /**
   * handles selected selection lists when enabled/disabled
   */
  const handleSelectionListActive = () => {
    handleInputChange('showSelectionList', !actionForm.showSelectionList);
    handleInputChange('selectionListId1', '');
    handleInputChange('selectionListId2', '');
  };

  /**
   * handles selection list change
   * @param {React.ChangeEvent<HTMLInputElement | HTMLSelectElement>} e
   * @param {number} listNumber
   */
  const handleSelectionListChange = (e, listNumber) => {
    if (listNumber === 1 && e.target.value === '') {
      handleInputChange('selectionListId1', '');
      handleInputChange('selectionListId2', '');
    } else {
      handleInputChange(`selectionListId${listNumber}`, e.target.value);
    }
  };

  /**
   * handles transaction change
   * @param {React.ChangeEvent<HTMLInputElement | HTMLSelectElement>} e
   */
  const handleTransactionChange = (e) => {
    setFormChanges((prev) => ({
      ...prev,
      transactionId: e.target.value,
      dockSelectionListId: '',
      dispositionId: '',
    }));
  };

  const handleOnHoldToggleChange = () => {
    handleInputChange('showOnHoldReason', !actionForm.showOnHoldReason);
  };

  return (
    <SetupDrawer
      open={open}
      close={handleClose}
      title='Action'
      isEdit={isEdit}
      isCopy={isCopyMode}
      active={actionForm.active}
      onActiveChange={() => handleInputChange('active', !actionForm.active)}
      onSubmit={handleSubmit}
      disableSubmit={disableSubmit}
    >
      {/* Routing Section */}
      <div className={classes.section}>
        <h3 className={`${classes.sectionTitle} ${classes.drawerHeader}`}>Routing</h3>
        <div className={classes.fieldGrid}>
          <Select
            className={classes.selectItem}
            labelText='Plant'
            id='PlantSelect'
            data-testid='plantSelect'
            value={isCopyMode ? localSelectedPlant : selectedPlant}
            disabled={!isCopyMode}
            onChange={handlePlantChange}
            required={isCopyMode}
          >
            <option value=''>--- Select an Option ---</option>
            {plants?.map((plant) => (
              <option key={plant.id} value={plant.id}>
                {plant.description}
              </option>
            ))}
          </Select>

          <Select
            className={classes.selectItem}
            labelText='Location'
            id='LocationSelect'
            data-testid='locationSelect'
            value={isCopyMode ? localSelectedLocation : selectedLocation}
            disabled={!isCopyMode}
            onChange={handleLocationChange}
            required={isCopyMode}
          >
            <option value=''>--- Select an Option ---</option>
            {locations?.map((location) => (
              <option key={location.id} value={location.id}>
                {location.description}
              </option>
            ))}
          </Select>

          <Select
            className={classes.selectItem}
            labelText='Queue Plant'
            id='QueuePlantSelect'
            required
            value={effectiveQueuePlantId}
            onChange={handleSelectedQueuePlantChange}
          >
            <option value=''>--- Select an Option ---</option>
            {plants?.map((plant) => (
              <option key={plant.id} value={plant.id}>
                {plant.description}
              </option>
            ))}
          </Select>

          <Select
            className={classes.selectItem}
            labelText='Queue'
            id='QueueSelect'
            required
            value={actionForm.queue?.id || ''}
            onChange={handleQueueChange}
          >
            <option value=''>--- Select an Option ---</option>
            {queues?.map((queue) => (
              <option key={queue.id} value={queue.id}>
                {queue.description}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {/* Display Section */}
      <div className={classes.section}>
        <h3 className={classes.sectionTitle}>Display</h3>
        <div className={classes.fieldGrid}>
          <TextInput
            className={classes.selectItem}
            labelText='Name'
            id='NameInput'
            required
            value={actionForm.description}
            maxLength={75}
            onChange={(/** @type {React.ChangeEvent<HTMLInputElement>} */ e) =>
              setFormChanges((prev) => ({ ...prev, description: e.target.value }))
            }
          />

          <TextInput
            className={classes.selectItem}
            labelText='Description'
            id='MessageInput'
            required
            value={actionForm.message}
            maxLength={100}
            onChange={(/** @type {React.ChangeEvent<HTMLInputElement>} */ e) =>
              setFormChanges((prev) => ({ ...prev, message: e.target.value }))
            }
          />

          <TextInput
            className={classes.selectItem}
            labelText='Sequence'
            id='SequenceSelect'
            name='sequence'
            value={actionForm.sequence}
            type='number'
            onChange={updateActionForm}
            error={formErrors.sequence ? 'Sequence must be greater than 0 and less than 1000' : undefined}
            max={1000}
          />
        </div>
      </div>

      {/* Inputs Section */}
      <div className={classes.section}>
        <h3 className={classes.sectionTitle}>Inputs</h3>
        <div className={classes.toggleGrid}>
          <div className={classes.toggleItem}>
            <span id='commentToggleLabel'>Comment</span>
            <Switch
              data-testid='commentToggle'
              checked={actionForm.showComment}
              onChange={() => handleInputChange('showComment', !actionForm.showComment)}
              aria-labelledby='commentToggleLabel'
            />
          </div>

          <div className={classes.toggleItem}>
            <span id='onHoldToggleLabel'>On Hold Reason</span>
            <Switch
              data-testid='showOnHoldToggle'
              checked={Boolean(actionForm.showOnHoldReason)}
              onChange={handleOnHoldToggleChange}
              aria-labelledby='onHoldToggleLabel'
            />
          </div>

          <div className={classes.toggleItem}>
            <span id='selectionListToggleLabel'>Selection List</span>
            <Switch
              data-testid='selectionListToggle'
              checked={actionForm.showSelectionList}
              onChange={handleSelectionListActive}
              aria-labelledby='selectionListToggleLabel'
            />
          </div>

          <div className={classes.toggleItem}>
            <span id='quantityToggleLabel'>Quantity</span>
            <Switch
              data-testid='quantityToggle'
              checked={actionForm.showQuantity}
              onChange={() => handleInputChange('showQuantity', !actionForm.showQuantity)}
              aria-labelledby='quantityToggleLabel'
            />
          </div>
        </div>

        <div className={classes.fieldGrid}>
          <div className={classes.inputToggleItem}>
            <span id='inputToggleLabel'>Input</span>
            <Switch
              data-testid='inputToggle'
              checked={actionForm.showDynamicInput}
              onChange={handleDynamicInput}
              aria-labelledby='inputToggleLabel'
            />
          </div>

          <TextInput
            labelText='Input Prompt'
            id='InputPromptInput'
            required={actionForm.showDynamicInput}
            value={actionForm?.actionPrompt || ''}
            maxLength={25}
            onChange={(/** @type {React.ChangeEvent<HTMLInputElement>} */ e) =>
              handleInputChange('actionPrompt', e.target.value)
            }
            className={classes.inputPromptItem}
            disabled={!actionForm.showDynamicInput}
            data-testid='inputPrompt'
          />
        </div>

        <div className={classes.fieldGrid}>
          <Select
            labelText='List 1'
            id='ListSelect1'
            value={actionForm.selectionListId1 || ''}
            onChange={(e) => handleSelectionListChange(e, 1)}
            className={classes.selectItem}
            disabled={!actionForm.showSelectionList}
            aria-labelledby='selectionListToggleLabel'
            data-testid='listSelect1'
          >
            <option value=''>--- Select List 1 ---</option>
            {selectionLists?.map((list) => (
              <option key={list.id} value={list.id}>
                {list.description}
              </option>
            ))}
          </Select>
          <Select
            labelText='List 2'
            id='ListSelect2'
            value={actionForm.selectionListId2 || ''}
            onChange={(e) => handleSelectionListChange(e, 2)}
            className={classes.selectItem}
            disabled={!actionForm.showSelectionList || actionForm.selectionListId1 === ''}
            aria-labelledby='selectionListToggleLabel'
            data-testid='listSelect2'
          >
            <option value=''>--- None ---</option>
            {selectionLists?.map((list) => (
              <option key={list.id} value={list.id}>
                {list.description}
              </option>
            ))}
          </Select>
        </div>
      </div>
      {/*Other */}
      <div className={classes.section}>
        <h3 className={classes.sectionTitle}>Other</h3>
        <div className={classes.fieldGrid}>
          <Select
            className={classes.selectItem}
            labelText='Transaction'
            id='TransactionSelect'
            value={actionForm.transactionId || ''}
            onChange={handleTransactionChange}
            data-testid='transactionSelect'
          >
            <option value=''>--- Select a Transaction ---</option>
            {transactions?.map((transaction) => (
              <option key={transaction.id} value={transaction.id}>
                {transaction.description}
              </option>
            ))}
          </Select>
          {actionForm.transactionId === 'TRAILER_TO_DOCK' && (
            <Select
              className={classes.selectItem}
              labelText='Dock Selection List'
              id='DockSelectionListSelect'
              value={actionForm.dockSelectionListId || ''}
              onChange={(e) => handleInputChange('dockSelectionListId', e.target.value)}
              data-testid='dockSelectionListSelect'
              required
            >
              <option value=''>Dock Selection List</option>
              {selectionLists?.map((list) => (
                <option key={list.id} value={list.id}>
                  {list.description}
                </option>
              ))}
            </Select>
          )}
          {actionForm.transactionId === 'ISSUE_TO_LINE' && (
            <Select
              className={classes.selectItem}
              labelText='Issue To'
              id='IssueToSelect'
              value={actionForm.dispositionId || ''}
              onChange={(e) => handleInputChange('dispositionId', e.target.value)}
              data-testid='issueToSelect'
              required
            >
              <option value=''>Issue To</option>
              {dispositions?.map((disposition) => (
                <option key={disposition.dispositionId} value={disposition.dispositionId}>
                  {disposition.description}
                </option>
              ))}
            </Select>
          )}
        </div>
        <div className={classes.fieldGrid}>
          <Select
            className={classes.selectItem}
            labelText='Query'
            id='QuerySelect'
            data-testid='querySelect'
            value={actionForm.actionQuery || ''}
            onChange={(e) => handleInputChange('actionQuery', e.target.value)}
          >
            <option value=''>--- Query ---</option>
            {QUERY_TYPES?.map((list) => (
              <option key={list.id} value={list.id}>
                {list.description}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {/* Linked Action Section */}
      <div className={classes.section}>
        <h3 className={classes.sectionTitle}>Linked Action</h3>
        <div className={classes.fieldGrid}>
          <Select
            className={classes.selectItem}
            labelText='Plant'
            id='LinkedActionPlantSelect'
            data-testid='linkedActionPlantSelect'
            value={effectiveLinkedActionPlant}
            onChange={handleLinkedActionPlantChange}
          >
            <option value=''>--- Select an Option ---</option>
            {plants?.map((plant) => (
              <option key={plant.id} value={plant.id}>
                {plant.description}
              </option>
            ))}
          </Select>

          <Select
            className={classes.selectItem}
            labelText='Location'
            id='LinkedActionLocationSelect'
            data-testid='linkedActionLocationSelect'
            value={effectiveLinkedActionLocation}
            onChange={handleLinkedActionLocationChange}
            disabled={!effectiveLinkedActionPlant}
          >
            <option value=''>--- Select an Option ---</option>
            {linkedLocationOptions?.map((location) => (
              <option key={location.id} value={location.id}>
                {location.description}
              </option>
            ))}
          </Select>
        </div>

        <div className={classes.fieldGrid}>
          <Select
            className={classes.selectItem}
            labelText='Action'
            id='LinkedActionSelect'
            data-testid='linkedActionSelect'
            value={effectiveLinkedActionId}
            onChange={(e) => setLinkedActionId(e.target.value)}
            disabled={!effectiveLinkedActionLocation}
          >
            <option value=''>--- Select an Option ---</option>
            {linkedActionOptions?.map((linked) => (
              <option key={linked.id} value={linked.id}>
                {linked.description}
              </option>
            ))}
          </Select>
        </div>
      </div>
    </SetupDrawer>
  );
};
